import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDetails, commentsList, defaultComment, type Comment } from '@/mocks/articleDetail';
import { hotArticles } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';

function CommentCard({ comment }: { comment: Comment }) {
  const avatarColors = ['bg-primary-500', 'bg-accent-500', 'bg-secondary-500', 'bg-foreground-700'];
  const colorIdx = comment.author.length % avatarColors.length;

  return (
    <div className="bg-background-50 rounded-lg p-4 md:p-5 border border-background-200/60">
      <div className="flex items-start gap-3">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full ${avatarColors[colorIdx]} flex items-center justify-center text-background-50 text-xs font-bold`}>
          {comment.author.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <i key={i} className={`w-3.5 h-3.5 flex items-center justify-center ${i < comment.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
            ))}
          </div>
          <h4 className="text-sm font-semibold text-foreground-900 mb-1">{comment.title}</h4>
          <p className="text-xs text-foreground-600 leading-relaxed">{comment.content}</p>
          <div className="flex items-center gap-3 mt-2.5">
            <span className="text-xs font-medium text-foreground-700">{comment.author}</span>
            <span className="text-xs text-foreground-400">{comment.timeAgo}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const article = slug ? articleDetails[slug] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 pb-10 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">文章不存在</h1>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = hotArticles.filter((a) => a.slug !== slug).slice(0, 3);
  const comments = slug === 'top-streamer-tax-evasion-2026' ? commentsList : defaultComment;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://911baoliao.com/" },
      { "@type": "ListItem", "position": 2, "name": article.category, "item": `https://911baoliao.com/category/${article.categorySlug}` },
      { "@type": "ListItem", "position": 3, "name": article.title, "item": `https://911baoliao.com/article/${article.slug}` }
    ]
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.subtitle,
    "image": article.heroImage,
    "author": {
      "@type": "Organization",
      "name": article.author,
      "url": "https://911baoliao.com/about"
    },
    "publisher": {
      "@type": "Organization",
      "name": "911爆料网",
      "logo": {
        "@type": "ImageObject",
        "url": "https://911baoliao.com/logo.png"
      }
    },
    "datePublished": article.publishDate,
    "dateModified": article.publishDate,
    "articleSection": article.category,
    "keywords": article.tags.join(", "),
    "url": `https://911baoliao.com/article/${article.slug}`,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://911baoliao.com/article/${article.slug}`
    },
    "breadcrumb": breadcrumbSchema
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "这篇文章的内容可信吗？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": article.isVerified ? "本文内容已通过编辑团队多重核实，标注为已核实爆料。" : "本文内容为网友投稿及公开信息整理，尚未完成全部核实流程，仅供参考。"
        }
      },
      {
        "@type": "Question",
        "name": "如何获取这篇文章的后续更新？",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "订阅911爆料网邮件推送，或在首页关注实时追踪板块，第一时间获取事件最新进展。"
        }
      }
    ]
  };

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[550px] flex items-end">
          <img
            src={article.heroImage}
            alt={article.title}
            title={`${article.title} - 911爆料网`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/65"></div>

          <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 pb-10 md:pb-14">
            <span className="inline-flex items-center px-3 py-1 rounded-full border border-white/30 text-white text-xs font-medium mb-3">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-white leading-tight max-w-3xl">
              {article.title}
            </h1>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="bg-background-50 border-b border-background-200/60">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-400 py-3 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-500 transition-colors">{article.category}</Link>
              <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center"></i>
              <span className="text-foreground-700">{article.title.slice(0, 20)}...</span>
            </nav>
          </div>
        </div>

        {/* Article Meta + Content */}
        <article className="py-8 md:py-10 bg-background-50">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            {/* Meta Info */}
            <div className="flex flex-wrap items-center gap-4 mb-8 pb-6 border-b border-background-200/60">
              <span className="text-sm font-medium text-foreground-700">{article.author}</span>
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
              <span className="flex items-center gap-1 text-xs text-accent-500 font-semibold">
                <i className="ri-fire-line w-3.5 h-3.5 flex items-center justify-center"></i>
                {article.heat} 热度
              </span>
              <span className="text-xs text-foreground-400">{article.views} 阅读</span>
              {article.isVerified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                  <i className="ri-verified-badge-line w-3 h-3 flex items-center justify-center"></i>
                  已核实
                </span>
              )}
            </div>

            {/* Subtitle */}
            <p className="text-base text-foreground-600 leading-relaxed mb-8 font-medium">
              {article.subtitle}
            </p>

            {/* Article Content */}
            <div className="prose-custom">
              {article.content.map((paragraph, i) => (
                <p key={i} className="text-sm text-foreground-800 leading-relaxed mb-5">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200/60">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/today-hot`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium whitespace-nowrap cursor-pointer hover:bg-secondary-200 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            {/* Share */}
            <div className="flex items-center gap-3 mt-6">
              <span className="text-xs text-foreground-400">分享到：</span>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                <i className="ri-weibo-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                <i className="ri-wechat-line w-4 h-4 flex items-center justify-center"></i>
              </button>
              <button className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-600 cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors" aria-label="复制链接">
                <i className="ri-link w-4 h-4 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </article>

        {/* Comments Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full bg-primary-500"></span>
              <span className="text-xs font-medium text-primary-500 uppercase tracking-wide">网友热评</span>
            </div>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-1">
              吃瓜群众 / 怎么说
            </h2>
            <p className="text-xs text-foreground-500 mb-8">看看大家对这件事的看法</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {comments.map((comment, index) => (
                <div key={comment.id} className={index >= comments.length - 2 ? 'md:opacity-70' : ''}>
                  <CommentCard comment={comment} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Articles */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6">
              相关爆料
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.id} article={a} variant="grid" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
              订阅追更
            </h2>
            <p className="mt-2 text-sm text-foreground-500 max-w-md mx-auto">
              不错过任何精彩爆料，第一时间接收推送通知
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-1.5 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 hover:bg-primary-600"
            >
              返回首页查看更多 <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
            <div className="mt-10 flex items-center justify-center gap-6 opacity-30">
              <i className="ri-camera-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              <i className="ri-flashlight-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
              <i className="ri-mic-line w-5 h-5 flex items-center justify-center text-foreground-400"></i>
            </div>
          </div>
        </section>

        {/* SEO Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema)
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema)
        }} />
      </main>
      <Footer />
    </>
  );
}